import { ShapeBase } from './object'
import createType, { AbstractType, NativeBaseOptional } from './type'

export const ExpressionOperationAvailable = {
  number: [
    {
      label: 'é igual a',
      value: {
        key: '=',
        kind: 'operator',
      },
    },
    {
      label: 'é diferente de',
      value: {
        key: '!=',
        kind: 'operator',
      },
    },
    {
      label: 'é menor ou igual a',
      value: {
        key: '<=',
        kind: 'operator',
      },
    },
    {
      label: 'é maior que',
      value: {
        key: '>',
        kind: 'operator',
      },
    },
    {
      label: 'é maior ou igual a',
      value: {
        key: '>=',
        kind: 'operator',
      },
    },
    {
      label: 'é menor que',
      value: {
        key: '<',
        kind: 'operator',
      },
    },
    {
      label: 'está entre',
      value: {
        key: 'BETWEEN',
        kind: 'operator',
      },
    },
    {
      label: 'não está entre',
      value: {
        key: 'NOT BETWEEN',
        kind: 'operator',
      },
    },
  ],
  string: [
    {
      label: 'é igual a',
      value: {
        key: '=',
        kind: 'operator',
      },
    },
    {
      label: 'é diferente de',
      value: {
        key: '!=',
        kind: 'operator',
      },
    },
  ],
  enumerated: [
    {
      label: 'é igual a',
      value: {
        key: '=',
        kind: 'operator',
      },
    },
    {
      label: 'é diferente de',
      value: {
        key: '!=',
        kind: 'operator',
      },
    },
  ],
  boolean: [
    {
      label: 'é',
      value: {
        key: '=',
        kind: 'operator',
      },
    },
  ],
}

export type ExpressionBinaryOperator =
  | '='
  | '!='
  | '<>'
  | '>'
  | '>='
  | '<'
  | '<='
  | 'LIKE'
  | 'NOT LIKE'
  | 'ILIKE'
  | 'NOT ILIKE'
  | 'IN'
  | 'NOT IN'
  | 'IS'
  | 'IS NOT'
  | 'BETWEEN'
  | 'NOT BETWEEN'
  | 'AND'
  | 'OR'
  | '+'
  | '-'
  | '*'
  | '/'
  | '%'
  | '^'
  | '||'
export type ExpressionUnaryOperator = '-' | '+' | 'NOT'
export type ExpressionFunction =
  | 'cast'
  | 'count'
  | 'sum'
  | 'avg'
  | 'max'
  | 'min'
  | 'any'
  | 'argMax'
  | 'upper'
  | 'lower'
  | 'length'
  | 'substring'
  | 'coalesce'
  | 'floor'
  | 'ceiling'
  | 'round'
  | 'now'
  | 'greatest'
  | 'least'
  | 'exists'

  // Geometry functions
  | 'setSrid'
  | 'centroid'
  | 'multi'
  | 'geomFromGeoJSON'
  | 'point'
  | 'dump'
  | 'area'
  | 'collect'
  | 'simplifyPreserveTopology'
  | 'makeValid'
  | 'intersects'
  | 'intersection'
  | 'getFaceGeometry'
  | 'geomFromEwkt'
  | 'contains'
  | 'isEmpty'
  | 'asGeoJSON'
  | 'pointOnSurface'
  | 'length'
  | 'lineMerge'
  | 'locateAlong'
  | 'addMeasure'
  | 'distance'
  | 'dwithin'
  | 'difference'
  | 'buffer'
  | 'azimuth'

export interface LiteralExpression {
  type: 'expression'
  kind: 'literal'
  value: string | number | boolean
  literalType: 'string' | 'number' | 'boolean'
  subType?: NativeBaseOptional
  label?: string
}

export type Expression<Property extends string | number | symbol = string> =
  | {
      type: 'expression'
      kind: 'binary'
      operator: ExpressionBinaryOperator
      left: Expression<Property>
      right: Expression<Property>
    }
  | {
      type: 'expression'
      kind: 'binary'
      operator: ExpressionBinaryOperator
      left: Expression<Property>
      right: {
        type: 'expression'
        kind: 'binary'
        operator: ExpressionBinaryOperator
        left: Expression<Property>
        right: Expression<Property>
      }
    }
  | {
      type: 'expression'
      kind: 'ternary'
      condition: {
        type: 'expression'
        kind: 'binary'
        operator: ExpressionBinaryOperator
        left: Expression<Property>
        right: Expression<Property>
      }
      whenTrue: Expression<Property>
      whenFalse: Expression<Property>
    }
  | {
      type: 'expression'
      kind: 'unary'
      operator: ExpressionUnaryOperator
      operand: Expression<Property>
    }
  | {
      type: 'expression'
      kind: 'summation'
      arguments: Expression<Property>[]
    }
  | {
      type: 'expression'
      kind: 'function'
      name: ExpressionFunction
      arguments: Expression<Property>[]
    }
  | {
      type: 'expression'
      kind: 'property'
      property: Property
      literalType?: 'string' | 'number' | 'boolean'
      subType?: NativeBaseOptional
    }
  | LiteralExpression
  | {
      type: 'expression'
      kind: 'null'
    }
  | {
      type: 'expression'
      kind: 'list'
      items: Expression<Property>[]
    }

export interface ExpressionType<
  ExpressionGeneric extends Expression<keyof Shape> | null = Expression,
  Shape extends ShapeBase = ShapeBase,
> extends AbstractType<ExpressionGeneric> {
  readonly type: 'expression'
  readonly shape: Shape
  readonly currentValue?: ExpressionGeneric
  isRequired(): ExpressionType<Exclude<ExpressionGeneric, null>, Shape>
  isOptional(): ExpressionType<ExpressionGeneric | null, Shape>
}

export default function expression<Shape extends ShapeBase = ShapeBase>(
  shape: Shape,
): ExpressionType<Expression<keyof Shape>, Shape> {
  return {
    ...createType<ExpressionType>(),
    type: 'expression',
    shape,
  } as ExpressionType<Expression<keyof Shape>, Shape>
}
